<script setup lang="ts">
import { computed } from "vue"
import UiButton from "@/components/ui/actions/UiButton.vue"
import UiSelect from "@/components/ui/forms/UiSelect.vue"
import type { UiDataTableColumn, UiDataTableFilter, UiPagination } from "@/types/ui"

type DataRow = Record<string, unknown>
type SortState = { key: string; direction: "asc" | "desc" }

const props = withDefaults(
  defineProps<{
    columns: UiDataTableColumn[]
    rows: DataRow[]
    search?: string
    filters?: UiDataTableFilter[]
    selectedFilters: Record<string, string | number | "">
    pagination: UiPagination
    loading?: boolean
    sort?: SortState | null
  }>(),
  {
    search: "",
    filters: () => [],
    selectedFilters: () => ({}),
    loading: false,
    sort: null,
  }
)

const emit = defineEmits<{
  (event: "update:search", value: string): void
  (event: "update:filter", payload: { key: string; value: string | number | "" }): void
  (event: "page-change", page: number): void
  (event: "per-page-change", perPage: number): void
  (event: "update:sort", value: SortState): void
}>()

const pages = computed(() => Math.max(1, Math.ceil(props.pagination.total / props.pagination.perPage)))
const effectiveSelectedFilters = computed(() => props.selectedFilters || {})
const from = computed(() => {
  if (props.pagination.total === 0) return 0
  return (props.pagination.page - 1) * props.pagination.perPage + 1
})
const to = computed(() => Math.min(props.pagination.page * props.pagination.perPage, props.pagination.total))

const perPageOptions = [10, 20, 50].map((value) => ({ label: `${value} por pagina`, value }))

function updateFilter(key: string, value: string | number | "") {
  emit("update:filter", { key, value })
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-"
  return String(value)
}

function isSorted(columnKey: string): boolean {
  return props.sort?.key === columnKey
}

function sortIndicator(columnKey: string): string {
  if (!isSorted(columnKey)) return "<>"
  return props.sort?.direction === "desc" ? "v" : "^"
}

function toggleSort(column: UiDataTableColumn) {
  if (!column.sortable) return

  const nextDirection = isSorted(column.key) && props.sort?.direction === "asc" ? "desc" : "asc"
  emit("update:sort", { key: column.key, direction: nextDirection })
}
</script>

<template>
  <section class="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex min-w-64 flex-1 flex-wrap gap-3">
        <div class="min-w-64 flex-1">
          <label class="mb-1 block text-sm font-medium text-slate-700" for="table-search">Pesquisar</label>
          <input
            id="table-search"
            :value="search"
            type="search"
            placeholder="Digite para buscar..."
            class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
            @input="emit('update:search', ($event.target as HTMLInputElement).value)"
          />
        </div>

        <div v-for="filter in filters" :key="filter.key" class="min-w-56 flex-1">
          <UiSelect
            :id="`filter-${filter.key}`"
            :label="filter.label"
            :options="filter.options"
            :model-value="effectiveSelectedFilters[filter.key] ?? ''"
            allow-empty
            empty-label="Todos"
            @update:model-value="updateFilter(filter.key, $event)"
          />
        </div>
      </div>

      <slot name="toolbar-actions" />
    </div>

    <div class="overflow-x-auto">
      <table class="min-w-full border-collapse">
        <thead>
          <tr class="border-b border-slate-200">
            <th
              v-for="column in columns"
              :key="column.key"
              class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
              :class="{
                'text-center': column.align === 'center',
                'text-right': column.align === 'right',
              }"
            >
              <button
                v-if="column.sortable"
                type="button"
                class="inline-flex items-center gap-1 text-inherit"
                @click="toggleSort(column)"
              >
                <span>{{ column.label }}</span>
                <span
                  class="text-[10px]"
                  :class="isSorted(column.key) ? 'text-slate-700' : 'text-slate-400'"
                >
                  {{ sortIndicator(column.key) }}
                </span>
              </button>
              <span v-else>{{ column.label }}</span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="loading">
            <td :colspan="columns.length" class="px-3 py-8 text-center text-sm text-slate-500">Carregando...</td>
          </tr>

          <tr v-else-if="rows.length === 0">
            <td :colspan="columns.length" class="px-3 py-8 text-center text-sm text-slate-500">Nenhum resultado encontrado.</td>
          </tr>

          <tr v-for="(row, index) in rows" v-else :key="index" class="border-b border-slate-100">
            <td
              v-for="column in columns"
              :key="column.key"
              class="px-3 py-2 text-sm text-slate-700"
              :class="{
                'text-center': column.align === 'center',
                'text-right': column.align === 'right',
              }"
            >
              <slot name="cell" :row="row" :column="column" :value="row[column.key]">
                {{ formatCellValue(row[column.key]) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <footer class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
      <p class="text-sm text-slate-600">Exibindo {{ from }}-{{ to }} de {{ pagination.total }}</p>

      <div class="flex items-center gap-2">
        <UiSelect
          id="table-per-page"
          label="Itens"
          :options="perPageOptions"
          :model-value="pagination.perPage"
          @update:model-value="emit('per-page-change', Number($event))"
        />
      </div>

      <div class="flex items-center gap-2">
        <UiButton variant="secondary" :disabled="pagination.page <= 1" @click="emit('page-change', pagination.page - 1)">
          Anterior
        </UiButton>
        <span class="text-sm text-slate-600">Pagina {{ pagination.page }} de {{ pages }}</span>
        <UiButton variant="secondary" :disabled="pagination.page >= pages" @click="emit('page-change', pagination.page + 1)">
          Proxima
        </UiButton>
      </div>
    </footer>
  </section>
</template>

